// src/components/AdminDashboard.tsx - FINAL: Recharts import fixed, endpoint graphs added.
import React, { useState, useCallback, memo, useMemo } from 'react';
import {
  Users, Activity, FileText, ArrowLeft, BarChart2, TrendingUp, Shield, Server, User, Clock, DollarSign
} from 'lucide-react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend
} from 'recharts';
// import type { TooltipProps } from 'recharts';
import { useAppContext } from '../hooks/useAppContext';
import AdminJobs from './AdminJobs';
import { UserOverview, JobDetails, LogDetails } from './AdminDetails';
import AdminReadState from './AdminReadState';
import AdminSpendView from './AdminSpendView';
import { useAdminRead } from '../hooks/useAdminRead';
import type { UserReference } from '../utils/admin';
import LogsViewer from './LogsViewer';
import AdminUserManagement from './AdminUserManagement';
import { formatLargeNumber } from '../utils';
import type { AdminAnalyticsSummaryResponse, User as UserType, AnalyticsTopUser, AnalyticsTopEndpoint, LogEntry, LogFilters, EndpointActivityOverTime } from '../types';
import './AdminDashboard.css';

type AdminTab = 'dashboard' | 'spend' | 'users' | 'jobs' | 'logs';

// #region Helper & Child Components

const StatCard = memo(({ label, value, icon, isLoading }: {
  label: string; value: string | number; icon: React.ReactNode; isLoading: boolean;
}) => (
  <div className="admin-card admin-stat-card admin-card__body">
    <div className="admin-stat-card__header">
      <div className="admin-stat-card__icon">{icon}</div>
      <div className="admin-stat-card__label">{label}</div>
    </div>
    {isLoading ? ( <div className="skeleton-text" style={{ width: '80px', height: '36px', marginTop: '4px' }} /> ) : (
      <div className="admin-stat-card__value">{typeof value === 'number' ? formatLargeNumber(value) : value}</div>
    )}
  </div>
));
StatCard.displayName = 'StatCard';

const CHART_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444', '#06b6d4', '#ec4899'];

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="chart-tooltip">
        <p className="tooltip-label">{new Date(label).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
        {payload.map((entry: any, index: number) => (
          <div key={entry.dataKey} className="tooltip-item" style={{ color: CHART_COLORS[index % CHART_COLORS.length] }}>
            {entry.name}: <strong>{formatLargeNumber(entry.value as number)}</strong>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

const TopUsersTable = memo(({ users, onUserClick }: { users: AnalyticsTopUser[], onUserClick: (user: AnalyticsTopUser) => void }) => (
  <div className="admin-card">
    <div className="admin-card__header"><h3><User size={16} /> Top Users by Requests</h3></div>
    <div className="table-wrapper-condensed">
      <table className="table-premium">
        <thead><tr><th>User Name</th><th>Request Count</th><th title="All application-recorded 4xx and 5xx responses, including throttling and cancellations.">Error Rate</th></tr></thead>
        <tbody>
          {users.map(user => (
            <tr key={user.owner_id} onClick={() => onUserClick(user)} className="clickable-row">
              <td>{user.user_name}</td>
              <td className="text-right">{formatLargeNumber(user.request_count)}</td>
              <td title={`${user.client_errors_4xx ?? 0} responses in 4xx; ${user.server_errors_5xx ?? 0} responses in 5xx`} className={`text-right ${user.error_rate > 0.05 ? 'text-danger' : ''}`}>{(user.error_rate * 100).toFixed(2)}%</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
));
TopUsersTable.displayName = 'TopUsersTable';

const TopEndpointsTable = memo(({ endpoints }: { endpoints: AnalyticsTopEndpoint[] }) => (
  <div className="admin-card">
    <div className="admin-card__header"><h3><Server size={16} /> Top Endpoints by Requests</h3></div>
    <div className="table-wrapper-condensed">
      <table className="table-premium">
        <thead><tr><th>Endpoint</th><th>Request Count</th><th>Avg. Response</th></tr></thead>
        <tbody>
          {endpoints.map(endpoint => (
            <tr key={endpoint.endpoint}>
              <td>{endpoint.endpoint}</td>
              <td className="text-right">{formatLargeNumber(endpoint.request_count)}</td>
              <td className="text-right">{endpoint.avg_response_time_ms.toFixed(1)}ms</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
));
TopEndpointsTable.displayName = 'TopEndpointsTable';

const EndpointChartsGrid = memo(({ endpointActivity }: { endpointActivity: EndpointActivityOverTime | undefined }) => {
    const chartData = useMemo(() => {
        if (!endpointActivity || !endpointActivity.timestamps || !endpointActivity.series) return [];
        return endpointActivity.timestamps.map((ts, i) => {
            const entry: { [key: string]: string | number } = { timestamp: ts };
            endpointActivity.series.forEach(s => {
                if (s && s.name) { entry[s.name] = s.data?.[i] || 0; }
            });
            return entry;
        });
    }, [endpointActivity]);

    if (!endpointActivity || !endpointActivity.series || endpointActivity.series.length === 0) {
        return <div className="no-data">No endpoint activity data available.</div>;
    }

    return (
        <div className="analytics-view__endpoint-grid">
            {endpointActivity.series.map((series, index) => (
                <div key={series.name} className="endpoint-chart-item">
                    <h4 className="endpoint-chart-title">{series.name}</h4>
                    <ResponsiveContainer width="100%" height={150}>
                        <LineChart data={chartData} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="var(--gray-200)" />
                            <XAxis dataKey="timestamp" stroke="var(--gray-500)" fontSize={10} tickFormatter={(ts) => new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} />
                            <YAxis stroke="var(--gray-500)" fontSize={10} allowDecimals={false} tickFormatter={(val) => formatLargeNumber(val as number)} />
                            <Tooltip content={<CustomTooltip />} />
                            <Line type="monotone" dataKey={series.name} name={series.name} stroke={CHART_COLORS[index % CHART_COLORS.length]} strokeWidth={2} dot={false} />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            ))}
        </div>
    );
});
EndpointChartsGrid.displayName = 'EndpointChartsGrid';


// #endregion

// #region Analytics View

const AdminAnalyticsView = memo(({ onUserClick }: { onUserClick: (user: AnalyticsTopUser | UserType) => void }) => {
  const [timeWindow, setTimeWindow] = useState(12);
  const [includePolling, setIncludePolling] = useState(false);
  const read = useAdminRead<AdminAnalyticsSummaryResponse>(`/api/admin/analytics/summary?hours=${timeWindow}${includePolling ? '' : '&exclude_endpoint=%2Fapi%2Fbilling%2Fbalance'}`);
  const data = read.data;
  const loading = read.loading;

  const summary = data?.overall_summary;
  const userActivity = data?.user_activity_over_time;
  const endpointActivity = data?.endpoint_activity_over_time;

  const userChartData = useMemo(() => {
    if (!userActivity || !userActivity.timestamps || !userActivity.series) return [];
    return userActivity.timestamps.map((ts, i) => {
      const entry: { [key: string]: string | number } = { timestamp: ts };
      userActivity.series.forEach(s => {
        if (s && s.name) { entry[s.name] = s.data?.[i] || 0; }
      });
      return entry;
    });
  }, [userActivity]);

  return (
    <div className="analytics-view">
      <AdminReadState {...read} hasData={!!data} />
      <div className="analytics-view__controls">
        <label className="admin-toggle"><input type="checkbox" checked={includePolling} onChange={e => setIncludePolling(e.target.checked)} /> Include balance polling</label>
        <button className="btn btn-secondary btn-sm" disabled={loading} onClick={read.refresh}>Refresh</button>
        <div className="segment-control" style={{ flex: 'none' }}>
          {[
            { hours: 1, label: '1H' }, { hours: 12, label: '12H' }, { hours: 24, label: '24H' },
            { hours: 168, label: '7D' }, { hours: 720, label: '30D' }
          ].map(({ hours, label }) => (
            <button key={hours} className={`segment-button analytics-time-button ${timeWindow === hours ? 'active' : ''}`} onClick={() => setTimeWindow(hours)}>
              {label}
            </button>
          ))}
        </div>
      </div>

      <p className="admin-footnote">Recorded API requests. Error rates include all 4xx and 5xx, including throttling and cancellations; latency is weighted by request count. User counts are approximate; charts show the most active users/endpoints and may omit others. In-flight and gateway-only failures are not included. Historical counts before October 16, 2026 (JST) may be incomplete.</p>
      <div className="analytics-view__stat-grid">
        <StatCard label="Total Requests" value={summary?.total_requests ?? 0} icon={<TrendingUp size={24} />} isLoading={loading} />
        <StatCard label="Unique Users" value={summary?.unique_users ?? 0} icon={<Users size={24} />} isLoading={loading} />
        <StatCard label="Avg Response Time" value={`${summary?.avg_response_time_ms.toFixed(1) ?? 0}ms`} icon={<Clock size={24} />} isLoading={loading} />
        <StatCard label="Error Rate" value={`${(((summary?.failed_requests ?? 0) / (summary?.total_requests || 1)) * 100).toFixed(2)}%`} icon={<Shield size={24} />} isLoading={loading} />
      </div>

      <div className="admin-card">
        <div className="admin-card__header"><h3>API Requests by User</h3></div>
        <div className="admin-card__body" style={{ height: 350 }}>
          {loading ? ( <div className="skeleton-text" style={{ width: '100%', height: '100%' }} /> ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={userChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--gray-200)" />
                <XAxis dataKey="timestamp" stroke="var(--gray-500)" fontSize={12} tickFormatter={(ts) => new Date(ts).toLocaleDateString([], { month: 'short', day: 'numeric' })} />
                <YAxis stroke="var(--gray-500)" fontSize={12} tickFormatter={(val) => formatLargeNumber(val as number)} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                {userActivity?.series.map((s, i) => (
                  <Line key={s.name} type="monotone" dataKey={s.name} stroke={CHART_COLORS[i % CHART_COLORS.length]} strokeWidth={2} dot={false} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
      
      <div className="analytics-view__table-grid">
        {loading ? (
          <>
            <div className="skeleton-text" style={{ width: '100%', height: '300px', borderRadius: 'var(--radius-card)' }} />
            <div className="skeleton-text" style={{ width: '100%', height: '300px', borderRadius: 'var(--radius-card)' }} />
          </>
        ) : (
          <>
            <TopUsersTable users={data?.top_users_by_requests || []} onUserClick={onUserClick} />
            <TopEndpointsTable endpoints={data?.top_endpoints_by_requests || []} />
          </>
        )}
      </div>
      
      <div className="admin-card">
        <div className="admin-card__header"><h3>Endpoint Activity Breakdown</h3></div>
        <div className="admin-card__body">
          {loading ? (
            <div className="analytics-view__endpoint-grid">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="skeleton-text" style={{ width: '100%', height: '180px', borderRadius: 'var(--radius)' }} />
              ))}
            </div>
          ) : (
            <EndpointChartsGrid endpointActivity={endpointActivity} />
          )}
        </div>
      </div>
    </div>
  );
});
AdminAnalyticsView.displayName = 'AdminAnalyticsView';

// #endregion

// #region Main Dashboard Component

const AdminDashboard: React.FC = () => {
  const { setView } = useAppContext();
  const [activeTab, setActiveTab] = useState<AdminTab>('dashboard');
  const [logFilters, setLogFilters] = useState<Partial<LogFilters> | null>(null);

  const [userReference, setUserReference] = useState<UserReference | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const [logDetail, setLogDetail] = useState<LogEntry | null>(null);
  const [jobsUser, setJobsUser] = useState('');
  const openUser = (reference: UserReference) => { setJobId(null); setLogDetail(null); setUserReference(reference); };
  const openSpendUser = useCallback((userId: string) => { setUserReference({ key: userId }); }, []);
  const handleUserClick = useCallback((user: AnalyticsTopUser | UserType) => {
    let identifier: string | null = null;
    if ('api_key' in user) { identifier = user.api_key; } 
    else if ('owner_id' in user) { identifier = user.owner_id; }
    if (identifier) {
      setUserReference({ key: identifier });
    }
  }, []);

  const renderActiveView = () => {
    switch (activeTab) {
      case 'dashboard': return <AdminAnalyticsView onUserClick={handleUserClick} />;
      case 'spend': return <AdminSpendView onUserClick={openSpendUser} />;
      case 'users': return <AdminUserManagement onUserClick={handleUserClick} />;
      case 'jobs': return <AdminJobs initialUser={jobsUser} onUser={openUser} onJob={setJobId} />;
      case 'logs': return <LogsViewer initialFilters={logFilters} onUser={openUser} onLog={setLogDetail} />;
      default: return <AdminAnalyticsView onUserClick={handleUserClick} />;
    }
  };

  return (
    <div className="admin-dashboard">
      <button onClick={() => setView('main')} className="admin-dashboard__back-button"><ArrowLeft size={16} /> Back to App</button>
      <header className="admin-dashboard__header">
        <h1>Admin Dashboard</h1>
        <p>System-wide monitoring and management tools.</p>
      </header>
      <nav className="admin-dashboard__tabs">
        <button className={`admin-tab-button ${activeTab === 'dashboard' ? 'active' : ''}`} onClick={() => setActiveTab('dashboard')}><BarChart2 size={16} /> Analytics</button>
        <button className={`admin-tab-button ${activeTab === 'spend' ? 'active' : ''}`} onClick={() => setActiveTab('spend')}><DollarSign size={16} /> Spend</button>
        <button className={`admin-tab-button ${activeTab === 'users' ? 'active' : ''}`} onClick={() => setActiveTab('users')}><Users size={16} /> User Management</button>
        <button className={`admin-tab-button ${activeTab === 'jobs' ? 'active' : ''}`} onClick={() => setActiveTab('jobs')}><Activity size={16} /> Job Activity</button>
        <button className={`admin-tab-button ${activeTab === 'logs' ? 'active' : ''}`} onClick={() => setActiveTab('logs')}><FileText size={16} /> API Logs</button>
      </nav>
      <main className="admin-dashboard__content">
        {renderActiveView()}
      </main>
      {userReference && <UserOverview reference={userReference} onClose={() => setUserReference(null)} onJob={id => { setUserReference(null); setJobId(id); }} onLogs={key => { setUserReference(null); setLogFilters({ userApiKey: key }); setActiveTab('logs'); }} onJobs={key => { setUserReference(null); setJobsUser(key); setActiveTab('jobs'); }} />}
      {jobId && <JobDetails id={jobId} onClose={() => setJobId(null)} onUser={openUser} />}
      {logDetail && <LogDetails log={logDetail} onClose={() => setLogDetail(null)} onUser={openUser} />}
    </div>
  );
};

export default AdminDashboard;